import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, ChevronUp } from 'lucide-react';

const DAYS = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
  { key: 'sat', label: '토' },
  { key: 'sun', label: '일' },
];

const DEFAULT_HOURS = {
  weekday: { open: '09:00', close: '22:00', closed: false },
  saturday: { open: '09:00', close: '18:00', closed: false },
  sunday: { open: '09:00', close: '18:00', closed: true },
};

function hoursToCustom(hours) {
  return {
    mon: { ...hours.weekday },
    tue: { ...hours.weekday },
    wed: { ...hours.weekday },
    thu: { ...hours.weekday },
    fri: { ...hours.weekday },
    sat: { ...hours.saturday },
    sun: { ...hours.sunday },
  };
}

function TimeRow({ label, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{label}</span>
        <button
          type="button"
          onClick={() => onChange({ ...value, closed: !value.closed })}
          className="flex items-center gap-2 group"
        >
          <span className={`text-xs ${value.closed ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            {value.closed ? '휴무' : '운영'}
          </span>
          <div className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${value.closed ? 'bg-gray-300 dark:bg-gray-600' : 'bg-green-500'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${value.closed ? 'translate-x-0.5' : 'translate-x-5'}`} />
          </div>
        </button>
      </div>
      {!value.closed && (
        <div className="flex items-center gap-2">
          <input
            type="time"
            className="input py-1.5 text-sm flex-1"
            value={value.open}
            onChange={e => onChange({ ...value, open: e.target.value })}
          />
          <span className="text-gray-400 text-sm">~</span>
          <input
            type="time"
            className="input py-1.5 text-sm flex-1"
            value={value.close}
            onChange={e => onChange({ ...value, close: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

export default function GymSetup() {
  const { user, profile, refreshGym, signOut } = useAuth();
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [custom, setCustom] = useState(false);
  const [customHours, setCustomHours] = useState(hoursToCustom(DEFAULT_HOURS));
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleGroupChange(key, value) {
    const updated = { ...hours, [key]: value };
    setHours(updated);
    setCustomHours(hoursToCustom(updated));
  }

  function handleCustomChange(day, value) {
    setCustomHours(prev => ({ ...prev, [day]: value }));
  }

  function toggleCustom() {
    if (!custom) setCustomHours(hoursToCustom(hours));
    setCustom(!custom);
  }

  function buildHoursPayload() {
    const base = custom ? { custom: true, ...customHours } : { custom: false, ...hours };
    if (notice.trim()) base.notice = notice.trim();
    return base;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    // 24시간 임시 체험 기간 적용 (어드민 승인 시 14일로 업데이트)
    const tempExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    const { error: insertError } = await supabase.from('gyms').insert({
      owner_id: user.id,
      name: profile?.business_name || '',
      phone: profile?.business_phone || null,
      address: profile?.business_address || null,
      business_hours: JSON.stringify(buildHoursPayload()),
      plan: 'trial',
      plan_expires_at: tempExpiry,
    });
    if (insertError) {
      setError(insertError.message);
      setLoading(false);
      return;
    }
    await refreshGym();
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <svg className="w-9 h-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
            </svg>
          </div>
          {/* 단계 인디케이터 */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <span className="text-xs text-gray-400">계정 설정</span>
            </div>
            <div className="w-8 h-px bg-primary-400" />
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">2</div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-200">헬스장 설정</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">헬스장 운영시간 설정</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">완료 후 24시간 무료 체험이 즉시 시작됩니다</p>
        </div>

        <div className="card p-6">
          {/* 등록 정보 확인 */}
          <div className="mb-5 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 space-y-1 text-sm">
            <p className="font-medium text-gray-900 dark:text-white">{profile?.business_name}</p>
            {profile?.business_phone && <p className="text-gray-500 dark:text-gray-400">{profile.business_phone}</p>}
            {profile?.business_address && <p className="text-gray-500 dark:text-gray-400">{profile.business_address}</p>}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="label mb-3">운영시간</p>
              <div className="space-y-3">
                <TimeRow label="평일 (월~금)" value={hours.weekday} onChange={v => handleGroupChange('weekday', v)} />
                <TimeRow label="토요일" value={hours.saturday} onChange={v => handleGroupChange('saturday', v)} />
                <TimeRow label="일요일" value={hours.sunday} onChange={v => handleGroupChange('sunday', v)} />
              </div>

              {/* 개별 설정 토글 */}
              <button
                type="button"
                onClick={toggleCustom}
                className="mt-3 flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline"
              >
                {custom ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {custom ? '개별 설정 닫기' : '요일별 개별 설정'}
              </button>

              {custom && (
                <div className="mt-3 space-y-2.5 pl-1 border-l-2 border-primary-200 dark:border-primary-800">
                  {DAYS.map(d => (
                    <TimeRow
                      key={d.key}
                      label={d.label + '요일'}
                      value={customHours[d.key]}
                      onChange={v => handleCustomChange(d.key, v)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 특별 안내 */}
            <div>
              <p className="label mb-1">특별 안내 <span className="text-xs text-gray-400 font-normal">(선택)</span></p>
              <textarea
                className="input resize-none"
                rows={3}
                placeholder={"예) 매달 둘째 주 일요일 휴무\n명절 연휴 (설, 추석) 휴무\n기타 특이사항을 자유롭게 입력하세요"}
                value={notice}
                onChange={e => setNotice(e.target.value)}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary w-full justify-center" disabled={loading}>
              {loading ? '시작하는 중...' : '서비스 시작하기 →'}
            </button>
          </form>
        </div>

        <p className="text-center mt-4">
          <button
            onClick={signOut}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
          >
            로그아웃
          </button>
        </p>
      </div>
    </div>
  );
}
